import {
  PDFDocument,
  rgb,
  setCharacterSpacing,
  breakTextIntoLines,
} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

class Pdf {
  pdfDoc;
  template;
  t;
  images = [];
  fonts = {
    Montserrat: {
      regular: 'montserrat/regular.ttf',
      bold: 'montserrat/bold.ttf',
    },
    Gotham: {
      regular: 'gotham/regular.otf',
      bold: 'gotham/bold.otf',
    },
    'Noto-Sans': {
      regular: 'noto_sans/en/regular.ttf',
      bold: 'noto_sans/en/bold.ttf',
    },
    'Noto-Sans-JP': {
      regular: 'noto_sans/jp/regular.ttf',
      bold: 'noto_sans/jp/bold.ttf',
    },
    'Noto-Sans-Thai': {
      regular: 'noto_sans/thai/regular.ttf',
      bold: 'noto_sans/thai/bold.ttf',
    },
    'Noto-Sans-TC': {
      regular: 'noto_sans/tc/regular.ttf',
      bold: 'noto_sans/tc/bold.ttf',
    },
    'Arial-Unicode': {
      regular: 'arial/regular.ttf',
      bold: 'arial/bold.ttf',
    },
  };
  fontToLoad = null;
  font = {
    regular: null,
    bold: null,
  };
  accessories = [];
  rooms = [];
  json = {};
  colours = {};

  constructor() {
    // this.pagePadding = 50;
    this.pagePaddingX = 50;
    this.pagePaddingYTop = 45;
    this.pagePaddingYBottom = 60;
    this.baseFontSize = 9;
    this.cellPaddingX = 20;
    this.cellPaddingY = 16;

    if (undefined === window['pdf']) {
      fetch('./locales/i18n/translation.json').then((response) => {
        response.json().then((json) => {
          this.json = {
            rooms: json['rooms'],
            accessories: json['accessories'],
          };

          this.fontToLoad = this.fonts[json['tool_ui']['general']['font']];
        });
      });
    } else {
      this.json = window['pdf'];
      this.fontToLoad = this.fonts[window['pdf']['tool_ui']['general']['font']];
    }
  }

  setAccessories(accessories) {
    this.accessories = accessories;
  }

  setRooms(rooms) {
    this.rooms = rooms;
  }

  setSections(sections) {
    this.sections = sections;
  }

  setTranslation(translation) {
    this.t = translation;
  }

  loadFont(name, weight) {
    return new Promise((resolve, reject) => {
      fetch(process.env.REACT_APP_FONTS_PATH + name)
        .then((res) => {
          res.arrayBuffer().then((arrayBuffer) => {
            this.pdfDoc
              .embedFont(arrayBuffer)
              .then((font) => {
                this.font[weight] = font;
                resolve();
              })
              .catch((e) => {
                this.loadFont('Montserrat', weight).then(() => {
                  resolve();
                });
              });
          });
        })
        .catch((e) => reject(e));
    });
  }

  loadAllFonts() {
    return [
      this.loadFont(
        this.fontToLoad
          ? this.fontToLoad.regular
          : this.fonts['Montserrat'].regular,
        'regular'
      ),
      this.loadFont(
        this.fontToLoad ? this.fontToLoad.bold : this.fonts['Montserrat'].bold,
        'bold'
      ),
    ];
  }

  loadLogo(logo) {
    return new Promise((resolve, reject) => {
      const logoImgUrl = `${process.env.PUBLIC_URL}/images_iax/icons/logo-${logo}.png`;

      fetch(logoImgUrl)
        .then((res) => {
          res.arrayBuffer().then((arrayBuffer) => {
            this.pdfDoc.embedPng(arrayBuffer).then((image) => {
              this.images[logo] = image;

              resolve();
            });
          });
        })
        .catch((e) => reject(e));
    });
  }

  loadImage(section) {
    return new Promise((resolve, reject) => {
      const imgUrl = `${process.env.PUBLIC_URL}/images_iax/icons/${section}.png`;

      fetch(imgUrl)
        .then((res) => {
          res.arrayBuffer().then((arrayBuffer) => {
            this.pdfDoc.embedPng(arrayBuffer).then((image) => {
              this.images[section] = image;

              resolve();
            });
          });
        })
        .catch((e) => reject(e));
    });
  }

  getBaseTemplate() {
    return new Promise((resolve, reject) => {
      fetch(process.env.REACT_APP_PDF_TEMPLATE)
        .then((res) => resolve(res.arrayBuffer()))
        .catch((e) => reject(e));
    });
  }

  loadImagesForPage(images) {
    return new Promise((resolve, reject) => {
      Promise.all(images).then(() => {
        resolve();
      });
    });
  }

  setup() {
    return new Promise((resolve, reject) => {
      this.getBaseTemplate()
        .then((template) => {
          // Load our PDF template
          PDFDocument.load(template).then((pdfTemplate) => {
            this.template = pdfTemplate;

            // Create our main PDF
            PDFDocument.create().then((pdfDoc) => {
              this.pdfDoc = pdfDoc;
              this.pdfDoc.registerFontkit(fontkit);

              Promise.all(this.loadAllFonts()).then(() => {
                resolve();
              });
            });
          });
        })
        .catch((e) => reject(e));
    });
  }

  linesOfTextAtSize(options) {
    const lines = breakTextIntoLines(
      options.text,
      this.pdfDoc.defaultWordBreaks,
      options.maxWidth,
      (t) => options.font.widthOfTextAtSize(t, options.fontSize)
    );

    return lines.length;
  }

  calculateCellHeight(adjustedWidth, text, fontSize, font, feature = false) {
    let cellWidth = adjustedWidth / 4;

    const lineHeight = 1.2;

    let totalLineCount;
    let cellHeight;

    if (feature) {
      let lineCounts = text.map((t) => {
        return this.linesOfTextAtSize({
          text: t,
          fontSize: fontSize,
          maxWidth: cellWidth - this.cellPaddingX * 2,
          font: font,
        });
      });
      totalLineCount = lineCounts.reduce((sum, item) => (sum += item), 0);

      // Features cell height = (number of lines * font size * lineheight) + ((number of featues - 1) * font size * lineheight) + padding
      cellHeight =
        totalLineCount * fontSize * lineHeight +
        (text.length - 1) * fontSize * lineHeight +
        this.cellPaddingY * 2;
    } else {
      totalLineCount = this.linesOfTextAtSize({
        text: text,
        fontSize: fontSize,
        maxWidth: cellWidth - this.cellPaddingX * 2,
        font: font,
      });
      cellHeight =
        totalLineCount * fontSize * lineHeight + this.cellPaddingY * 2;
    }

    return cellHeight;
  }

  calculateRowHeight(
    adjustedWidth,
    rowData,
    font,
    fontSize,
    isHeaders = false
  ) {
    let cellHeights;

    if (!isHeaders) {
      const formattedFeatures = rowData.features.map((feature) =>
        this.t(feature)
      );
      const formattedRecommended = rowData.recommended_name
        ? this.t(rowData.recommended_name)
        : '';

      const poweredBy =
        rowData.powered_by === 'MULTI'
          ? `, ${this.t('tool_ui.general.poweredBy')}`
          : rowData.powered_by === 'SINGLE'
          ? ` ${this.t('tool_ui.general.poweredBy')}`
          : '';

      const rowContent = [
        this.t(rowData.name),
        this.t(rowData.description),
        formattedRecommended + poweredBy,
      ];

      const featuresCellHeight = this.calculateCellHeight(
        adjustedWidth,
        formattedFeatures,
        fontSize,
        font,
        true
      );

      cellHeights = rowContent.map((item) => {
        return this.calculateCellHeight(adjustedWidth, item, fontSize, font);
      });

      cellHeights.push(featuresCellHeight);
    } else {
      cellHeights = rowData.map((item) => {
        return this.calculateCellHeight(adjustedWidth, item, fontSize, font);
      });
    }
    return Math.max(...cellHeights);
  }

  drawColumn(basePage, currentX, startY, rowHeight) {
    basePage.drawLine({
      start: {
        x: currentX,
        y: startY,
      },
      end: {
        x: currentX,
        y: startY - rowHeight,
      },
      thickness: 0.5,
      color: rgb(196 / 255, 190 / 255, 182 / 255),
    });
  }

  drawRow(basePage, currentX, currentRowTopY, adjustedWidth, rowHeight = 0) {
    basePage.drawLine({
      start: { x: currentX, y: currentRowTopY - rowHeight },
      end: {
        x: currentX + adjustedWidth,
        y: currentRowTopY - rowHeight,
      },
      thickness: 0.5,
      color: rgb(196 / 255, 190 / 255, 182 / 255),
    });
  }

  getTemplate() {
    return this.template;
  }

  getPageFromTemplate() {
    return new Promise((resolve, reject) => {
      this.pdfDoc
        .copyPages(this.getTemplate(), [0])
        .then((clone) => {
          resolve(clone);
        })
        .catch((e) => reject(e));
    });
  }

  getLastPageAdded() {
    // Get all pages
    const pages = this.pdfDoc.getPages();
    pages[pages.length - 1].pushOperators(setCharacterSpacing(1));

    return pages[pages.length - 1];
  }

  startNewPage() {
    return new Promise((getNewPage) => {
      this.getPageFromTemplate().then((newPage) => {
        this.pdfDoc.addPage(newPage[0]);

        getNewPage(this.getLastPageAdded());
      });
    });
  }

  addContent() {
    return new Promise((pageDone) => {
      let images = [];

      this.sections.forEach((section) => {
        images.push(this.loadImage(section));
      });

      images.push(this.loadLogo('lenovo'));
      images.push(this.loadLogo('intel'));

      this.loadImagesForPage(images).then(() => {
        this.getPageFromTemplate().then((newPage) => {
          this.pdfDoc.addPage(newPage[0]);

          let basePage = this.getLastPageAdded();

          const { width, height } = basePage.getSize();

          const adjustedWidth = width - 2 * this.pagePaddingX;
          const adjustedHeight =
            height - this.pagePaddingYTop - this.pagePaddingYBottom;

          let currentX = this.pagePaddingX;
          let currentY = height - this.pagePaddingYTop - 10;

          // Add Lenovo Logo
          basePage.drawImage(this.images['lenovo'], {
            x: width - 140,
            y: currentY - 75,
            width: 140,
            height: 94,
          });

          // Create Titles
          const titleFontSize = this.baseFontSize * 2.1;
          const titleTopY = currentY - titleFontSize;

          const titleText = this.t('tool_ui.final.title');
          const subtitleText = this.t('tool_ui.final.text');

          // Create Title
          basePage.pushOperators(setCharacterSpacing(-1.2));
          basePage.drawText(titleText, {
            x: currentX,
            y: titleTopY,
            size: titleFontSize,
            lineHeight: titleFontSize,
            font: this.font.bold,
            color: rgb(0, 0, 0),
          });

          // Create Subtitle
          const subtitleMarginTop = 15;
          const subtitleLineHeight = 1.2;

          const subtitleY = titleTopY - this.baseFontSize - subtitleMarginTop;

          basePage.pushOperators(setCharacterSpacing(0));
          basePage.drawText(subtitleText, {
            x: currentX,
            y: subtitleY,
            size: this.baseFontSize,
            font: this.font.regular,
            color: rgb(0, 0, 0),
            maxWidth: adjustedWidth * 0.7,
            wrap: true,
            lineHeight: this.baseFontSize * subtitleLineHeight,
          });

          const subtitlesHeight =
            this.linesOfTextAtSize({
              text: subtitleText,
              fontSize: this.baseFontSize,
              maxWidth: adjustedWidth * 0.7,
              font: this.font.regular,
            }) *
            this.baseFontSize *
            subtitleLineHeight;

          // SECTIONS
          const sectionsMarginsY = 20; // sections container
          const sectionMarginsY = 15; // individual section
          const sectionTitleFontSize = this.baseFontSize * 1.8;

          let currentSectionTop =
            subtitleY - subtitlesHeight - sectionsMarginsY;

          // let pageResetSection = false;
          let aIdx = 0;
          let sIdx = 0;

          const nextSection = () => {
            const section = this.sections[sIdx];
            aIdx = 0;

            if (!section) {
              return;
            }

            const sectionAccessories = this.accessories.filter(
              (accessory) => accessory.room === section
            );

            // SECTION - titles
            const sectionTitleMarginBottom = 40;
            const sectionTitle = this.t(
              this.rooms.find((room) => room.key === section).title
            );

            let currentSectionTitleY = currentSectionTop - sectionTitleFontSize;

            const imageWidth = 30;
            const imageMarginRight = 10;

            // SECTION - icon
            basePage.drawImage(this.images[section], {
              x: currentX,
              y:
                currentSectionTitleY -
                (imageWidth + 5) / 2 +
                sectionTitleFontSize / 2,
              width: imageWidth,
              height: imageWidth,
            });

            // SECTION - header
            basePage.pushOperators(setCharacterSpacing(-1));
            basePage.drawText(sectionTitle, {
              x: currentX + imageWidth + imageMarginRight,
              y: currentSectionTitleY,
              size: sectionTitleFontSize,
              font: this.font.bold,
              color: rgb(0, 0, 0),
            });

            // TABLE
            const tableHeaders = [
              'Solution',
              'Description',
              'Benefits',
              'Recommended offering',
            ];

            let cellWidth = adjustedWidth / 4;
            let headerCellPaddingY = 10;

            // TABLE - headers
            let tableTopY = currentSectionTitleY - sectionTitleMarginBottom;
            let tableHeadersY = tableTopY - this.baseFontSize;

            const headersRowHeight = this.calculateRowHeight(
              adjustedWidth,
              tableHeaders,
              this.font.bold,
              this.baseFontSize,
              true
            );

            let currentCellX = currentX;
            let currentCellTextX = currentX;

            for (const header of tableHeaders) {
              if (header !== 'Solution') {
                this.drawColumn(
                  basePage,
                  currentCellX,
                  tableTopY,
                  headersRowHeight
                );
              }
              currentCellX += cellWidth;

              basePage.pushOperators(setCharacterSpacing(-0.5));
              basePage.drawText(header, {
                x: currentCellTextX,
                y: tableHeadersY,
                size: this.baseFontSize,
                font: this.font.bold,
                color: rgb(67 / 255, 67 / 255, 67 / 255),
              });

              if (header === 'Solution') {
                currentCellTextX += cellWidth + this.cellPaddingX;
              } else {
                currentCellTextX += cellWidth;
              }
            }

            // TABLE - content
            currentCellX = currentX; // reset X
            currentCellTextX = currentX; // reset X text

            let currentRowTopY = tableHeadersY - headerCellPaddingY;
            let currentRowTextY =
              tableHeadersY -
              headerCellPaddingY -
              this.cellPaddingY -
              this.baseFontSize;

            const nextAccessory = () => {
              const accessory = sectionAccessories[aIdx];

              if (!accessory) {
                return;
              }

              const rowHeight = this.calculateRowHeight(
                adjustedWidth,
                accessory,
                this.font.regular,
                this.baseFontSize
              );

              const {
                name,
                description,
                features,
                recommended_name,
                powered_by,
              } = accessory;

              // "Solution" column
              basePage.pushOperators(setCharacterSpacing(-0.3));
              basePage.drawText(this.t(name), {
                x: currentCellTextX,
                y: currentRowTextY,
                font: this.font.bold,
                size: this.baseFontSize * 1.28,
                color: rgb(0, 0, 0),
                maxWidth: cellWidth - this.cellPaddingX,
                lineHeight: this.baseFontSize * 1.28 * 1.2,
                wrap: true,
              });

              currentCellX += cellWidth; // Update X
              currentCellTextX += cellWidth + this.cellPaddingX; // Update X for next cell
              this.drawColumn(
                basePage,
                currentCellX,
                currentRowTopY,
                rowHeight
              );

              // "Description" column
              basePage.pushOperators(setCharacterSpacing(0));
              basePage.drawText(this.t(description), {
                x: currentCellTextX,
                y: currentRowTextY,
                font: this.font.regular,
                size: this.baseFontSize,
                color: rgb(0, 0, 0),
                maxWidth: cellWidth - 2 * this.cellPaddingX,
                lineHeight: this.baseFontSize * 1.2,
                wrap: true,
              });

              currentCellX += cellWidth; // Update X
              currentCellTextX += cellWidth;
              this.drawColumn(
                basePage,
                currentCellX,
                currentRowTopY,
                rowHeight
              );

              // "Features" column
              let featureY = currentRowTextY;
              let gap = this.baseFontSize;

              for (let i = 0; i < features.length; i++) {
                let featureHeight =
                  this.linesOfTextAtSize({
                    text: this.t(features[i]),
                    fontSize: this.baseFontSize,
                    maxWidth: cellWidth - this.cellPaddingX * 2,
                    font: this.font.regular,
                  }) *
                    this.baseFontSize *
                    1.2 +
                  gap;

                basePage.drawText(this.t(features[i]), {
                  x: currentCellTextX,
                  y: featureY,
                  font: this.font.regular,
                  size: this.baseFontSize,
                  color: rgb(0, 0, 0),
                  maxWidth: cellWidth - 2 * this.cellPaddingX,
                  lineHeight: this.baseFontSize * 1.2,
                  wrap: true,
                });
                featureY -= featureHeight;
              }

              currentCellX += cellWidth; // Update X
              currentCellTextX += cellWidth;
              this.drawColumn(
                basePage,
                currentCellX,
                currentRowTopY,
                rowHeight
              );

              let updatedRecommended = this.t(recommended_name);

              if (powered_by === 'MULTI') {
                updatedRecommended += `, ${this.t(
                  'tool_ui.general.poweredby'
                )}`;
              } else if (powered_by === 'SINGLE') {
                updatedRecommended += ` ${this.t('tool_ui.general.poweredby')}`;
              }

              // "Recommended" column
              basePage.drawText(updatedRecommended, {
                x: currentCellTextX,
                y: currentRowTextY,
                font: this.font.regular,
                size: this.baseFontSize,
                color: rgb(0, 0, 0),
                maxWidth: cellWidth - 2 * this.cellPaddingX,
                lineHeight: this.baseFontSize * 1.2,
                wrap: true,
              });

              // top row border
              if (accessory === sectionAccessories[0]) {
                this.drawRow(basePage, currentX, currentRowTopY, adjustedWidth);
              }

              // Row borders
              this.drawRow(
                basePage,
                currentX,
                currentRowTopY,
                adjustedWidth,
                rowHeight
              );

              // Update Y
              currentRowTopY -= rowHeight;
              currentRowTextY -= rowHeight;
              currentY = currentRowTopY;

              // Reset X
              currentCellX = currentX;
              currentCellTextX = currentX;

              currentSectionTop = currentY - sectionMarginsY * 2;

              // NEXT ACCESSORY
              if (aIdx < sectionAccessories.length - 1) {
                aIdx++;

                let nextRowHeight = this.calculateRowHeight(
                  adjustedWidth,
                  sectionAccessories[aIdx],
                  this.font.regular,
                  this.baseFontSize
                );

                if (currentY - nextRowHeight < this.pagePaddingYBottom) {
                  this.startNewPage().then((newPage) => {
                    basePage = newPage; // new page

                    currentY = adjustedHeight + this.pagePaddingYTop - 10; // reset Y to page top
                    currentRowTopY = currentY;
                    currentRowTextY =
                      currentRowTopY - this.cellPaddingY - this.baseFontSize;

                    this.drawRow(
                      basePage,
                      currentX,
                      currentRowTopY,
                      adjustedWidth
                    ); // row top border

                    nextAccessory();
                  });
                } else {
                  nextAccessory();
                }
              } else if (sIdx < this.sections.length - 1) {
                // NEXT SECTION
                sIdx++;

                let sectionAcc = this.accessories.filter(
                  (accessory) => accessory.room === this.sections[sIdx]
                );

                let nextRowHeight = this.calculateRowHeight(
                  adjustedWidth,
                  sectionAcc[0],
                  this.font.regular,
                  this.baseFontSize
                );

                let remainingSpace =
                  currentSectionTop - headersRowHeight - nextRowHeight >
                  this.pagePaddingYBottom;

                if (!remainingSpace) {
                  this.startNewPage().then((newPage) => {
                    basePage = newPage;

                    currentY = height - this.pagePaddingYTop;
                    currentSectionTop = currentY;

                    nextSection();
                  });
                } else {
                  nextSection();
                }
              } else {
                // Add Intel Logo
                basePage.drawImage(this.images['intel'], {
                  x: this.pagePaddingX,
                  y: 15,
                  width: 81,
                  height: 45,
                });

                // FINISH
                pageDone();
              }
            };
            nextAccessory();
          };
          nextSection();
        });
      });
    });
  }

  process() {
    return new Promise((resolve, reject) => {
      this.setup()
        .then(() => {
          this.addContent().then(() => {
            resolve(this.pdfDoc.save());
          });
        })
        .catch((e) => reject(e));
    });
  }

  save(output) {
    window.open(
      URL.createObjectURL(new Blob([output], { type: 'application/pdf' }))
    );
  }
}

export default Pdf;
